<template>
  <div class="usage-section">
    <rest-list
      ref="list"
      :payload="fetchEndpoint"
      class="usage-table"
      @dataLoaded="onDataLoaded"
    >
      <rest-list-column
        prop="campaign_title"
        :title="$t('EmailRows_Table_LinkedCampaignName')"
        :min-width="200"
        :sortable="false"
      >
        <template v-slot="scope">
          <router-link
            v-if="canViewRow(scope.row)"
            :to="editRoute(scope.row)"
            :data-qa="
              `linked-${
                scope.row.is_campaign_template
                  ? 'campaign-workflow-template'
                  : 'campaign-workflow'
              }-${scope.row.campaign_id}`
            "
          >
            {{ scope.row.campaign_title }}
          </router-link>
          <template v-else>
            {{ scope.row.campaign_title }}
          </template>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="is_campaign_template"
        :title="$t('EmailRows_Table_Type')"
        :min-width="150"
        :sortable="false"
        :filter-input="false"
        :filter-options="typeOptions"
        filter-field="is_campaign_template"
      >
        <template v-slot="scope">
          {{ typeLabel(scope.row) }}
          <span v-if="scope.row.is_draft" class="draft-badge">
            {{ $t('EmailRows_Table_WorkflowDraft') }}
          </span>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="action_name"
        :title="$t('EmailRows_Table_ActionName')"
        :min-width="150"
        :sortable="false"
      />

      <rest-list-column
        prop="variant_key"
        :title="$t('EmailRows_Table_Variant')"
        :min-width="80"
        :sortable="false"
        :filter-input="false"
        :filter-options="variantOptions"
      />

      <rest-list-column-date
        prop="synced_at"
        :title="$t('EmailRows_Table_LastSynced')"
        :min-width="200"
        :unix="false"
        :sortable="false"
        date-format="DD/MM/YYYY HH:mm"
      />

      <rest-list-column
        prop="is_stale"
        :title="$t('EmailRows_Table_SyncStatus')"
        :min-width="100"
        :sortable="false"
        :filter-input="false"
        :filter-options="syncStatusOptions"
        filter-field="is_stale"
      >
        <template v-slot="scope">
          <el-tag
            :type="scope.row.is_stale ? 'warning' : 'success'"
            size="small"
          >
            {{
              scope.row.is_stale
                ? $t('EmailRows_Table_SyncStatus_Stale')
                : $t('EmailRows_SyncStatus_InSync')
            }}
          </el-tag>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="synced_content_version"
        :title="$t('EmailRows_Table_SyncedVersion')"
        :min-width="55"
        :sortable="false"
        :filterable="false"
      />

      <rest-list-column
        prop="action_id"
        :title="$t('EmailRows_Table_Languages')"
        :min-width="120"
        :filterable="false"
        :sortable="false"
      >
        <template v-slot="scope">
          <el-button
            size="small"
            :data-qa="`view-languages-${scope.row.action_id}`"
            @click="openLanguages(scope.row)"
          >
            {{ $t('EmailRows_Action_ViewLanguages') }}
          </el-button>
        </template>
      </rest-list-column>
    </rest-list>

    <synced-row-languages-dialog
      :visible.sync="languagesDialogVisible"
      :content-id="contentId"
      :action-id="selectedActionId"
    />
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import SyncedRowLanguagesDialog from '@/modules/email-saved-rows/components/SyncedRowLanguagesDialog'
import { projectEndpoint } from '@/core/helpers'
import { campaignContentRoute } from '@/modules/email-saved-rows/helpers/campaignContentRoute'
import syncedRowTypeMixin from '@/modules/email-saved-rows/mixins/syncedRowTypeMixin'

export default {
  name: 'SyncedRowWorkflowCampaignsUsageTable',
  components: {
    RestList,
    RestListColumn,
    RestListColumnDate,
    SyncedRowLanguagesDialog
  },

  mixins: [syncedRowTypeMixin],

  props: {
    contentId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      loadedRows: [],
      languagesDialogVisible: false,
      selectedActionId: null
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `content/${this.contentId}/usage-messages`,
        params: {
          type: 'workflow-campaigns'
        }
      })
    },

    variantOptions() {
      const opts = {}
      this.loadedRows.forEach(m => {
        if (m.variant_key) opts[m.variant_key] = m.variant_key
      })
      return opts
    },

    syncStatusOptions() {
      return {
        stale: this.$t('EmailRows_Table_SyncStatus_Stale'),
        synced: this.$t('EmailRows_SyncStatus_InSync')
      }
    },

    canEdit() {
      if (this.$isRole('dpo') || this.$isRole('csa')) {
        return false
      }

      return this.$can('updateProjectItem')
    }
  },

  methods: {
    refresh() {
      this.$refs.list.refresh(true)
    },

    onDataLoaded(data) {
      this.loadedRows = data || []
    },

    openLanguages(row) {
      this.selectedActionId = row.action_id
      this.languagesDialogVisible = true
    },

    editRoute(row) {
      const path = row.is_campaign_template
        ? '/push/campaign/template'
        : '/push/campaign'
      return campaignContentRoute(
        path,
        {
          id: row.campaign_id,
          project_id: this.$route.query.project_id,
          channel: 'workflow',
          variant: row.variant_key
        },
        this.canEdit
      )
    }
  }
}
</script>
<style lang="scss" scoped>
.draft-badge {
  background: #a855f7;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 5px;
}
</style>



// WEBPACK FOOTER //
// src/modules/email-saved-rows/components/SyncedRowWorkflowCampaignsUsageTable.vue